import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Seo, ORGANIZATION_SCHEMA, SOFTWARE_APP_SCHEMA, buildFaqSchema } from "../components/Seo";
import { FAQ_ITEMS } from "../components/FaqSection";
import { PartnerBand } from "../components/PartnerBand";
import { ArrowUpRight, Globe, FileText, Sparkles, Shield, ChevronDown } from "lucide-react";

const HERO_RESUME = "https://static.prod-images.emergentagent.com/jobs/6df76809-4fec-43bf-818b-1c3433308751/images/7154ceb38950377fbec40ac3a1ba4d64b40b3967dc8583ddcb24f4da8fe9b502.png";

const EASE = [0.22, 1, 0.36, 1];
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.85, delay, ease: EASE },
});

const RECRUITER_MARKS = [
  { label: "Strong metric impact", style: "gold", top: "12%", right: "-6%" },
  { label: "Clear progression", style: "emerald", top: "42%", right: "-10%" },
  { label: "Recruiter pick", style: "", top: "68%", right: "-4%" },
];

const TRUST_LOGOS = ["Google", "Stripe", "Meta", "Spotify", "McKinsey", "BCG", "Apple", "Atlassian"];

const Landing = () => {
  const [stats, setStats] = useState({ templates: 200 });
  const { user } = useAuth();

  useEffect(() => {
    api.get("/templates").then((r) => setStats((s) => ({ ...s, templates: r.data.count || 200 }))).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen surface-dark">
      <Seo
        title="PrestiqueResume — Recruiter-ready resumes for ambitious professionals"
        description="The modern professional identity layer. Build credible, recruiter-ready resumes — enhanced by AI, never fabricated. 20 editorial foundations, fully customisable, global hiring compatibility, ATS-aware."
        path="/"
        schema={[ORGANIZATION_SCHEMA, SOFTWARE_APP_SCHEMA, buildFaqSchema(FAQ_ITEMS)]}
      />
      <Header variant="dark" />

      {/* HERO — cinematic split, parallax resume + recruiter marks */}
      <section className="relative overflow-hidden">
        <div className="spotlight-glow" />
        <div className="grain-dark absolute inset-0" />
        <div className="relative max-w-[1480px] mx-auto px-5 sm:px-8 lg:px-12 pt-20 pb-32 lg:pt-28 lg:pb-40">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <motion.div {...fadeUp(0)} className="eyebrow-gold mb-7" data-testid="hero-eyebrow">
                ◆ For ambitious candidates · 140+ countries
              </motion.div>
              <motion.h1
                {...fadeUp(0.08)}
                className="font-display text-[clamp(2.4rem,5.2vw,4.6rem)] leading-[1.02] tracking-[-0.02em] text-warm-ivory max-w-[18ch]"
                data-testid="hero-headline"
              >
                The modern professional <span className="editorial-accent">identity layer</span>.
              </motion.h1>
              <motion.p
                {...fadeUp(0.20)}
                className="mt-8 max-w-xl text-lg leading-[1.65] text-warm-ivory/65"
              >
                Recruiter-ready resumes and CVs that read as credible, considered, and human.
                Enhanced by AI — never fabricated by it. Built for global hiring.
              </motion.p>
              <motion.div {...fadeUp(0.32)} className="mt-11 flex flex-wrap items-center gap-5">
                <Link to={user ? "/dashboard" : "/signup"} data-testid="hero-cta-primary">
                  <Button className="btn-gold rounded-full h-12 px-8 font-mono uppercase tracking-[0.18em] text-[11px]">
                    Start your career edition <ArrowUpRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link
                  to="/templates"
                  data-testid="hero-cta-secondary"
                  className="font-mono uppercase tracking-[0.18em] text-[11px] text-warm-ivory/70 underline underline-offset-8 decoration-warm-ivory/15 hover:decoration-burnished-gold hover:text-burnished-gold transition-colors"
                >
                  See {stats.templates} editorial templates
                </Link>
              </motion.div>

              {/* Tiny trust strip */}
              <motion.div {...fadeUp(0.44)} className="mt-14 flex flex-wrap items-center gap-x-7 gap-y-2 text-[10px] font-mono uppercase tracking-[0.22em] text-warm-ivory/40">
                <span className="inline-flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-burnished-gold" />ATS-aware</span>
                <span className="inline-flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-burnished-gold" />Recruiter-tested</span>
                <span className="inline-flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-burnished-gold" />GDPR-secure</span>
                <span className="inline-flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-burnished-gold" />Zero AI fabrication</span>
              </motion.div>
            </div>

            {/* Hero artifact: resume document with recruiter annotations */}
            <motion.div
              initial={{ opacity: 0, y: 36, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 1.2, ease: EASE, delay: 0.15 }}
              className="lg:col-span-5 relative"
            >
              <div className="relative">
                <div className="absolute -inset-12 bg-gradient-to-br from-burnished-gold/12 via-transparent to-deep-navy/30 blur-3xl pointer-events-none" />
                <div className="relative rounded-2xl overflow-hidden border border-warm-ivory/10 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.7)]">
                  <img
                    src={HERO_RESUME}
                    alt="Recruiter-ready resume preview"
                    className="w-full h-auto block"
                    data-testid="hero-resume-image"
                  />
                </div>
                {/* Floating recruiter marks */}
                {RECRUITER_MARKS.map((m, i) => (
                  <motion.div
                    key={m.label}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.0 + i * 0.18, duration: 0.6, ease: EASE }}
                    className={`absolute recruiter-mark ${m.style}`}
                    style={{ top: m.top, right: m.right }}
                    data-testid={`recruiter-mark-${i}`}
                  >
                    ✎ {m.label}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CREDIBILITY MARQUEE — sober monochrome */}
      <section className="border-y border-warm-ivory/8 bg-deep-graphite/40">
        <div className="max-w-[1480px] mx-auto px-5 sm:px-8 lg:px-12 py-8">
          <div className="flex items-center gap-6">
            <div className="eyebrow-gold whitespace-nowrap hidden sm:block">Trusted across</div>
            <div className="flex-1 overflow-hidden">
              <div className="flex gap-12 animate-marquee" style={{ width: "max-content" }}>
                {[...TRUST_LOGOS, ...TRUST_LOGOS].map((name, i) => (
                  <span key={i} className="font-display italic text-2xl text-warm-ivory/35 whitespace-nowrap">{name}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE PILLARS — bento grid */}
      <section className="relative">
        <div className="max-w-[1480px] mx-auto px-5 sm:px-8 lg:px-12 py-28 lg:py-40">
          <motion.div {...fadeUp(0)} className="max-w-3xl mb-20">
            <div className="eyebrow-gold mb-5">Why PrestiqueResume</div>
            <h2 className="font-display text-[clamp(2rem,3.6vw,3.25rem)] leading-[1.08] tracking-[-0.02em] text-warm-ivory">
              Designed for credibility. <span className="editorial-accent">Engineered for recruiters.</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Big card */}
            <motion.div {...fadeUp(0)} className="lg:col-span-2 lg:row-span-2 bento-card p-10 lg:p-14 relative overflow-hidden">
              <div className="absolute inset-0 opacity-[0.06] bg-gradient-to-br from-burnished-gold to-transparent" />
              <div className="relative">
                <Sparkles className="w-7 h-7 text-burnished-gold mb-8" strokeWidth={1.4} />
                <h3 className="font-display text-3xl lg:text-4xl leading-[1.15] mb-5 text-warm-ivory">
                  AI-enhanced storytelling, <span className="editorial-accent">never AI-generated nonsense</span>.
                </h3>
                <p className="text-warm-ivory/65 leading-relaxed max-w-2xl">
                  Claude rewrites bullets in your voice. No phantom employers. No invented metrics.
                  No rounded-up dates. Just considered prose that holds up to recruiter scrutiny.
                </p>
                <div className="mt-10 grid sm:grid-cols-2 gap-4 max-w-lg">
                  {[
                    "Four tones: professional, confident, warm, concise",
                    "Tailor for any job description, instantly",
                    "Sanity-checked against your own history",
                    "Zero hallucinated facts — guaranteed",
                  ].map((b) => (
                    <div key={b} className="flex gap-2.5 text-sm text-warm-ivory/70">
                      <span className="text-burnished-gold mt-1.5">◆</span>
                      <span>{b}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Side cards */}
            <motion.div {...fadeUp(0.1)} className="bento-card p-10 relative">
              <Shield className="w-6 h-6 text-burnished-gold mb-6" strokeWidth={1.4} />
              <h3 className="font-display text-2xl mb-3 text-warm-ivory">Recruiter-readiness</h3>
              <p className="text-warm-ivory/60 text-sm leading-relaxed">
                Every template tested against the real applicant tracking systems used by Fortune 500 recruiters.
                No keyword stuffing. No invisible tricks.
              </p>
            </motion.div>

            <motion.div {...fadeUp(0.18)} className="bento-card p-10 relative">
              <Globe className="w-6 h-6 text-burnished-gold mb-6" strokeWidth={1.4} />
              <h3 className="font-display text-2xl mb-3 text-warm-ivory">Global hiring readiness</h3>
              <p className="text-warm-ivory/60 text-sm leading-relaxed">
                CV conventions for UK, US, EU, MENA & APAC markets. Tailored positioning for international roles.
              </p>
            </motion.div>

            <motion.div {...fadeUp(0.04)} className="bento-card p-10 relative">
              <FileText className="w-6 h-6 text-burnished-gold mb-6" strokeWidth={1.4} />
              <h3 className="font-display text-2xl mb-3 text-warm-ivory">Editorial templates</h3>
              <p className="text-warm-ivory/60 text-sm leading-relaxed">
                {stats.templates} layouts designed like a quality publication. No HR dashboards. No generic SaaS.
              </p>
            </motion.div>

            <motion.div {...fadeUp(0.12)} className="bento-card p-10 relative lg:col-span-2">
              <div className="flex items-start gap-6">
                <div>
                  <div className="eyebrow-gold mb-3">Editorial principle</div>
                  <h3 className="font-display text-2xl mb-3 text-warm-ivory leading-tight">
                    We won't lock you in.
                  </h3>
                  <p className="text-warm-ivory/60 text-sm leading-relaxed max-w-md">
                    Build for free. Preview every template. Export only when ready. Cancel any time.
                    Your data is yours — exportable, deletable, sovereign.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* THREE REFUSALS — editorial band */}
      <section className="relative bg-deep-graphite/60 border-y border-warm-ivory/8">
        <div className="grain-dark absolute inset-0" />
        <div className="relative max-w-[1480px] mx-auto px-5 sm:px-8 lg:px-12 py-24 lg:py-32">
          <motion.h2 {...fadeUp(0)} className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.1] mb-16 max-w-3xl text-warm-ivory">
            Three things we <span className="editorial-accent">refuse</span> to do.
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-12 lg:gap-16">
            {[
              { n: "I", t: "Fabricate.", b: "AI may polish your voice. It will never invent a role, a company, a date, or a result that didn't happen." },
              { n: "II", t: "Game ATS.", b: "No invisible keyword stuffing. No white-on-white tricks. Just clean documents that pass real applicant tracking systems honestly." },
              { n: "III", t: "Trap you.", b: "Build free. Export only when ready. Cancel any time. Your data is portable. GDPR compliant by default." },
            ].map((p, i) => (
              <motion.div key={p.n} {...fadeUp(i * 0.12)}>
                <div className="font-display italic text-7xl text-soft-bronze mb-5 leading-none">{p.n}</div>
                <div className="font-display text-2xl leading-tight mb-4 text-warm-ivory">{p.t}</div>
                <p className="text-warm-ivory/55 text-sm leading-[1.7]">{p.b}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="relative">
        <div className="max-w-[1480px] mx-auto px-5 sm:px-8 lg:px-12 py-32 lg:py-44">
          <div className="max-w-3xl">
            <motion.div {...fadeUp(0)} className="eyebrow-gold mb-7">Start today</motion.div>
            <motion.h2 {...fadeUp(0.08)} className="font-display text-[clamp(2.2rem,4.4vw,4rem)] leading-[1.05] tracking-[-0.02em] text-warm-ivory">
              Your next interview <span className="editorial-accent">starts with how you're read.</span>
            </motion.h2>
            <motion.p {...fadeUp(0.18)} className="mt-8 text-lg text-warm-ivory/65 leading-[1.65] max-w-xl">
              Free to start. Free to preview every template. Pay only when you export.
              Used by candidates across 140+ countries.
            </motion.p>
            <motion.div {...fadeUp(0.28)} className="mt-11 flex flex-wrap items-center gap-5">
              <Link to={user ? "/dashboard" : "/signup"} data-testid="closing-cta">
                <Button className="btn-gold rounded-full h-12 px-8 font-mono uppercase tracking-[0.18em] text-[11px]">
                  Start your career edition <ArrowUpRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link
                to="/pricing"
                className="font-mono uppercase tracking-[0.18em] text-[11px] text-warm-ivory/70 underline underline-offset-8 decoration-warm-ivory/15 hover:decoration-burnished-gold hover:text-burnished-gold transition-colors"
              >
                See plans
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partner band — JobVibe credibility */}
      <PartnerBand />

      {/* FAQ teaser — links to dedicated page */}
      <section className="border-t border-warm-ivory/8">
        <div className="max-w-[1480px] mx-auto px-5 sm:px-8 lg:px-12 py-20">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="eyebrow-gold mb-4">Questions answered</div>
              <h3 className="font-display text-2xl sm:text-3xl tracking-[-0.02em] leading-tight text-warm-ivory">
                Read our position on AI, ATS, and privacy.
              </h3>
            </div>
            <Link to="/faq" className="font-mono uppercase tracking-[0.18em] text-[11px] text-burnished-gold underline underline-offset-8 decoration-burnished-gold/40 hover:decoration-burnished-gold inline-flex items-center gap-2">
              Read the FAQ <ChevronDown className="w-3.5 h-3.5 -rotate-90" />
            </Link>
          </div>
        </div>
      </section>

      <Footer variant="dark" />
    </div>
  );
};

export default Landing;
